import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { cn } from "@/lib/utils";

/**
 * shadcn-style tooltip wrapper around Radix' primitives. Use it like :
 *
 *   <Tooltip>
 *     <TooltipTrigger asChild><button>?</button></TooltipTrigger>
 *     <TooltipContent>Explanation…</TooltipContent>
 *   </Tooltip>
 *
 * The provider is mounted once at the app root (see main.tsx) so triggers
 * can share the same hover-grace delay. Content uses our `surface` and
 * `border` tokens so it stays readable in both light and dark themes.
 */
const TooltipProvider = TooltipPrimitive.Provider;
const Tooltip = TooltipPrimitive.Root;
const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 6, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      data-bourso-anim="tooltip"
      className={cn(
        "z-50 max-w-xs rounded-md border border-border-strong bg-surface px-3 py-2 text-xs text-foreground",
        className
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
